import React from "react";
import { useApp } from "@/context/AppContext";
import { useFetch, StatCard, Loading, fmtMAD } from "@/components/common";
import { Card } from "@/components/ui/card";
import {
  Users, UserCheck, UserX, Wallet, TrendingDown, Scale, Package,
  Dumbbell, Swords, Trophy, FileWarning, ShieldAlert, FileX,
} from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6", "#EC4899", "#6366F1"];

export default function Dashboard() {
  const { user, t } = useApp();
  const { data: s, loading } = useFetch("/dashboard/stats", []);
  if (loading || !s) return <Loading />;

  const cards = [
    { label: t("total_players"), value: s.total_players, icon: Users, tone: "emerald" },
    { label: t("active_players"), value: s.active_players, icon: UserCheck, tone: "blue", sub: `${s.inactive_players} inactive` },
    { label: t("unpaid_fees"), value: s.unpaid_players, icon: UserX, tone: "rose", sub: "this month" },
    { label: t("monthly_revenue"), value: fmtMAD(s.monthly_revenue), icon: Wallet, tone: "emerald" },
    { label: t("total_expenses"), value: fmtMAD(s.total_expenses), icon: TrendingDown, tone: "amber" },
    { label: t("net_balance"), value: fmtMAD(s.net_balance), icon: Scale, tone: s.net_balance >= 0 ? "emerald" : "rose" },
    { label: t("equipment_stock"), value: s.equipment_stock, icon: Package, tone: "violet", sub: `${s.low_stock} low stock` },
    { label: t("upcoming_matches"), value: s.upcoming_matches, icon: Swords, tone: "blue" },
    { label: t("upcoming_trainings"), value: s.upcoming_trainings, icon: Dumbbell, tone: "emerald" },
    { label: t("upcoming_tournaments"), value: s.upcoming_tournaments, icon: Trophy, tone: "amber" },
    { label: t("missing_documents"), value: s.missing_documents, icon: FileWarning, tone: "rose" },
    { label: t("j6_status"), value: s.no_j6, icon: FileX, tone: "amber", sub: `${s.no_insurance} no insurance` },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold font-heading tracking-tight">{t("welcome")}, {user?.name} 👋</h1>
        <p className="text-muted-foreground mt-1">{t("overview")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats">
        {cards.map((c, i) => <StatCard key={c.label} {...c} index={i} testid={`stat-${i}`} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-6">
          <h3 className="font-bold font-heading mb-4">{t("players_by_category")}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={s.players_by_category}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <h3 className="font-bold font-heading mb-4">{t("players_by_team")}</h3>
          {s.players_by_team?.length && s.total_players > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={s.players_by_team} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(e) => `${e.name} (${e.value})`}>
                  {s.players_by_team.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="h-[280px] flex items-center justify-center text-muted-foreground">{t("no_data")}</div>}
        </Card>
      </div>
    </div>
  );
}
